import { FC, Fragment, ReactElement } from "react";
import { styled } from "@mui/material/styles";
import { useStudySettings } from "@/infrastructure/adapters/user-preference/user-preference.hook";
import { parseFurigana } from "@/utils/furigana";

type FuriganaTextProps = {
  /** Japanese learning content, optionally containing inline `[base]{reading}` ruby syntax */
  text?: string | null;
};

/**
 * Renders Japanese learning content with optional furigana (ruby) annotations.
 *
 * Caller commits to passing Japanese source text — translated/localized strings
 * (EN/VN/etc.) should bypass this component and render as plain text.
 *
 * Reading rendered as `<rt>` only when `learner.isFuriganaEnabled` is true;
 * when disabled, raw `[base]{reading}` syntax is still stripped — only the
 * base text shows. Composes inside MUI `<Typography>`.
 *
 * Wrapped in a `styled` `<span>` with `overflow-wrap: anywhere`: on iOS WebKit,
 * `<ruby>` annotations inflate the content's min-content intrinsic width, so a
 * flex-item container (e.g. `flex: 1` question text) refuses to shrink and the
 * text overflows and is clipped by an ancestor `overflow: hidden`. Unlike
 * `break-word`, `anywhere` is counted when computing min-content, letting the
 * container shrink to the viewport so long Japanese passages wrap correctly on
 * narrow devices (iPhone SE). See IROHANA_STUDY_DEV-400.
 */
export const FuriganaText: FC<FuriganaTextProps> = ({ text }): ReactElement | null => {
  const { settings } = useStudySettings();
  const showFurigana = settings?.isFuriganaEnabled ?? false;

  if (!text) return null;

  const segments = parseFurigana(text);

  return (
    <TextWrapper>
      {segments.map((segment, i) => {
        if (segment.type === "text") {
          return <Fragment key={i}>{segment.value}</Fragment>;
        }
        return (
          <ruby key={i} style={{ rubyAlign: "center" }}>
            {segment.base}
            {showFurigana && <rt style={{ fontSize: "0.6em" }}>{segment.reading}</rt>}
          </ruby>
        );
      })}
    </TextWrapper>
  );
};

const TextWrapper = styled("span")({
  overflowWrap: "anywhere",
});
